import React, { useState } from 'react';
import { connect } from 'react-redux';

// Components
import { ContactPlanAccountsEmptyState, ContactPlanAccountsHeader } from './components';
import { PlanAccountsTable } from '@/views/containers/planning/data-tables/PlanAccountsTable';
import DefaultEditContactPortfolioModal from '@/views/components/modals/portfolio-modals/DefaultEditContactPortfolioModal';
import UnlinkContactModal from '@/views/containers/planning/partials/UnlinkContactModal';

// Hooks
import { useActions, useData, useSearch, useTableMenuItems } from './hooks';

// Redux Actions
import { attachProfileToPortfolio, loadPortfolioForEdit, resetPortfolio, setPortfolioContact, setPortfolioPlanAccount } from '@/redux/modules/edit-portfolio';
import { logNewPortfolio } from '@/services/usage-log';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';

// Types
import { CurrentContact } from '@/redux/modules/types/contacts';
import Nullable from '@/types/nullable';
import { Account, Plan } from '@/types/planning';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import { ReduxState } from '@/redux/modules/types';
import EnterpriseDetailInfo from '@/types/enterprise-detail-info';

// Utils
import { LOCALE } from '@/utils/i18n';
import { filterImportedAccounts } from '@/utils/planning';
import LoadingScreen from '@/views/components/common/LoadingScreen';

export type DispatchProps = {
  attachProfileToPortfolio: (profile: Partial<PortfolioRiskProfile>) => void;
  loadPortfolioForEdit: (portfolio: Portfolio) => void;
  logNewPortfolio: () => void;
  resetPortfolio: () => void;
  setPortfolioContact: (contact: GenericContact) => void;
  setPortfolioPlanAccount: (planId: string, accountId: string, account?: Account) => void;
  togglePortfolioEditor: (visible: boolean) => void;
};

type StateProps = {
  locale: string;
  enterprise?: EnterpriseDetailInfo;
};

type Props = {
  currentContact: CurrentContact;
} & DispatchProps &
  StateProps;

export const ContactPlanAccounts: React.FC<Props> = ({ currentContact, locale, enterprise, ...dispatchProps }: Props) => {
  const { tableColumns, i18nBasePath, displayContent, isLoading, plan, importedAccounts } = useData({ currentContact });
  const { onCreatePortfolio, onGoToImportAccounts, onRemovePlanAccount, onSavePortfolio, onUnlinkPlan } = useActions({
    currentContact,
    dispatchProps,
    plan,
    accounts: plan?.accounts ?? [],
    i18nBasePath,
  });
  const { searchQuery, setQuery, filteredData } = useSearch({ accounts: importedAccounts, locale: locale as LOCALE });
  const { menuItems } = useTableMenuItems({ i18nBasePath, onRemovePlanAccount });
  const [showUnlinkContactModal, setShowUnlinkContactModal] = useState(false);
  const contactId = currentContact?.contact?._id;

  if (isLoading) {
    return <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.GetPlanForContact.Title`} />;
  }

  return (
    <>
      <ContactPlanAccountsEmptyState
        hasAccounts={importedAccounts.length > 0}
        planId={plan?._id}
        hasPlan={!!(plan?._id && plan?.contactId === contactId)}
        enterprise={enterprise}
      />
      {displayContent && (
        <div className="contact-plan-accounts table-wrapper table-container">
          <ContactPlanAccountsHeader
            i18nBasePath={i18nBasePath}
            currentContact={currentContact}
            hasAccounts={(plan?.accounts ?? []).length > 0}
            setQuery={setQuery}
            onGoToImportAccounts={onGoToImportAccounts}
            onUnlinkContact={() => setShowUnlinkContactModal(true)}
          />
          <div className="contact-plan-accounts-body">
            <PlanAccountsTable
              columns={tableColumns}
              data={filteredData}
              menuItems={menuItems}
              buttonActions={{ onCreatePortfolio }}
              i18nBasePath={i18nBasePath}
              searchQuery={searchQuery}
              isSelectable={false}
            />
          </div>
        </div>
      )}
      <DefaultEditContactPortfolioModal onSave={onSavePortfolio} showActionOnSave />

      {showUnlinkContactModal && plan && (
        <UnlinkContactModal
          onCancel={() => setShowUnlinkContactModal(false)}
          onConfirm={() => {
            onUnlinkPlan(plan?._id!);
            setShowUnlinkContactModal(false);
          }}
          show={showUnlinkContactModal}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
  enterprise: state.auth.user?.enterprise,
});

const mapDispatchToProps = {
  attachProfileToPortfolio,
  loadPortfolioForEdit,
  logNewPortfolio,
  resetPortfolio,
  setPortfolioContact,
  setPortfolioPlanAccount,
  togglePortfolioEditor,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactPlanAccounts);
